import React, { useEffect, useState } from 'react';
import { InputNumber, Tooltip } from 'antd';
import { CanvassItemRecord } from '../../../types/Canvass';

/**
 * The quantity of one material, edited in place.
 *
 * In place rather than behind a drawer because a canvass can carry a hundred
 * rows and the quantities often arrive as zeros -- opening and closing a form
 * for each would make filling them in the slowest part of the job.
 *
 * Zero is shown as a warning rather than a value: it is what a missing cost
 * datasheet looks like by the time it reaches here, and it cannot be ordered
 * against.
 */
const QtyCell = ({
  item,
  editable,
  onSave,
}: {
  item: CanvassItemRecord;
  editable: boolean;
  onSave: (quantity: number | null) => void;
}) => {
  const [value, setValue] = useState<number | null>(item.quantity ?? null);

  // Follow the record when it changes underneath us (a save refetches).
  useEffect(() => {
    setValue(item.quantity ?? null);
  }, [item.quantity]);

  const missing = value === null || value === undefined || value === 0;

  if (!editable) {
    return (
      <span className={missing ? 'text-amber-600' : ''}>
        {missing ? 'not set' : value}{' '}
        <span className={'text-gray-400'}>{item.uom?.description ?? ''}</span>
      </span>
    );
  }

  return (
    <Tooltip title={missing ? 'No quantity costed upstream — enter it here' : undefined}>
      <div className={'flex items-center gap-1'}>
        <InputNumber
          size={'small'}
          min={0}
          value={value}
          status={missing ? 'warning' : undefined}
          className={'w-24'}
          onChange={(v) => setValue(v as number | null)}
          // Saved on blur and on Enter, not on every keystroke: a hundred rows
          // being typed into should not be a hundred requests.
          onBlur={() => {
            if ((value ?? null) !== (item.quantity ?? null)) onSave(value);
          }}
          onPressEnter={() => {
            if ((value ?? null) !== (item.quantity ?? null)) onSave(value);
          }}
        />
        <span className={'text-gray-400 text-xs'}>{item.uom?.description ?? ''}</span>
      </div>
    </Tooltip>
  );
};

export default QtyCell;
